import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';
import { DevicesService } from '../resources/devices.service';
import { DatabaseService } from '../storage/database.service';
import { WebSocketsService } from '../websockets/websockets.service';
import { IWebSocketDeviceMessage } from '../websockets/websocket-device-message.interface';
import { PluginFactoryService } from '../plugins/plugin-factory.service';
import { SmartenitPlugin } from '../plugins/smartenit-plugin';
import { Model } from '../common/model';
import { DeviceState } from './device-state.enum';
import { DeviceReferenceService } from '../common/device-reference.service';

import _ from 'lodash';

export class DeviceModel extends Model {
  private _plugins: any;
  private _type: string;
  private _model: string;
  private _processors: any;
  private _loading: boolean;
  private _online: boolean;
  private _cachedData: boolean;
  private _onlineTimeout: any;
  private _ownerId: string;
  private _parents: any;
  private _state: string;
  private _image: any;

  private static _requests: any = {};
  private static _totalRequestCount: number = 0;

  private _deviceRequests: any = null;
  private firstRequest: boolean = true;

  private queueInterval: any;
  private deviceSocketSubscription: Subscription;
  private DOORLOCK_DELAY: number = 6;
  private SECOND: number = 1000;

  get image(): any {
    return this._image;
  }

  set image(img: any) {
    this._image = img;
  }

  get processors(): any {
    return this._processors;
  }

  get model(): string {
    return this._model;
  }

  get meta(): any {
    return this._data.meta;
  }

  set meta(value: any) {
    this._data.meta = value;
  }

  get settings(): any {
    return this._data.settings;
  }

  set settings(value: any) {
    this._data.settings = value;
  }

  get type(): string {
    return this._type;
  }

  get plugins(): any {
    return this._plugins;
  }

  get account(): any {
    return this._data.accountId;
  }

  get domain(): any {
    return this._data.domain;
  }

  get interfaces(): any {
    return this._data.interfaces;
  }

  get hwId(): boolean {
    return this._data.hwId;
  }

  get loading(): boolean {
    return this._loading;
  }

  set loading(value: boolean) {
    this._loading = value;
  }

  get online(): boolean {
    return this._online;
  }

  get cached(): boolean {
    return this._cachedData;
  }

  set cached(value: boolean) {
    this._cachedData = value;

    this._onDeviceOnlineState.next({
      cached: this._cachedData,
      online: this._online
    });
  }

  get ownerId(): string {
    return this._ownerId;
  }

  get parents(): any {
    return this._parents;
  }

  private _onDeviceData: Subject<IWebSocketDeviceMessage>;

  get onDeviceData(): Observable<IWebSocketDeviceMessage> {
    return this._onDeviceData.asObservable();
  }

  private _onDeviceOnlineState: Subject<any>;

  get onDeviceOnlineState(): Observable<any> {
    return this._onDeviceOnlineState.asObservable();
  }

  private _onDeviceResponse: Subject<any>;

  get onDeviceResponse(): Observable<any> {
    return this._onDeviceResponse.asObservable();
  }

  private _onDeviceStateChanged: Subject<any>;

  get onDeviceStateChanged(): Observable<any> {
    return this._onDeviceStateChanged.asObservable();
  }

  get supportsDRLC(): boolean {
    if (!this.getProcessor) {
      return false;
    }
    if (!this.deviceReferenceService.isLoaded) {
      this.deviceReferenceService.loadFile();
    }
    const procesor = this.getProcessor('DRLCClient');
    const models: any = _.get(this.deviceReferenceService, 'data.models', {});
    const model = _.get(this, 'config.model', '').toLowerCase();
    const def: any[] = [];
    const groups = _.get(this.deviceReferenceService, 'data.groups', def);
    const group = groups.find((g) => {
      if (Array.isArray(g.models) && g.models.indexOf(model) != -1 ) {
        return g.use['drlc-optin-out'] === true;
      }
      return false;
    });
    return procesor && ((models[model] && models[model].use && models[model].use['drlc-optin-out'] == true ) || group) ;
  }

  protected static getPluginKey(
    componentId: string,
    processorName: string
  ): string {
    if (componentId && processorName) {
      return ['comp', componentId, 'proc', processorName].join('_');
    }

    return '';
  }

  public rediscover(): Observable<any> {
    return this.devicesService.post(`${this._id}/rediscover`, {});
  }

  constructor(
    data: any,
    protected devicesService: DevicesService,
    protected dbService: DatabaseService,
    public webSocketsService: WebSocketsService,
    protected pluginFactory: PluginFactoryService,
    private deviceReferenceService: DeviceReferenceService
  ) {
    super(devicesService, data);
    this._onDeviceData = new Subject<IWebSocketDeviceMessage>();
    this._onDeviceResponse = new Subject<any>();
    this._onDeviceOnlineState = new Subject<any>();
    this._onDeviceStateChanged = new Subject<any>();
    this._loading = false;
    this._online = false;
    this._plugins = {};
    this._processors = [];

    this.loadBasicInfo(this, data);
    this.loadPluginsAndProcessors(this, data);
    this.queueInterval = setInterval(() => {
      this.drainQueue();
    }, 2 * 1000);
  }

  public loadBasicInfo(deviceInstance: DeviceModel, data: any) {
    if (data && data.hasOwnProperty('_id')) {
      deviceInstance.__id = data._id;
      deviceInstance._type =
        data.config && data.config.type ? data.config.type : data.type;
      deviceInstance._model =
        data.config && data.config.model ? data.config.model : null;
      deviceInstance._ownerId = data.ownerId;
      deviceInstance._parents = data.parents;

      if (deviceInstance._state != data.state) {
        deviceInstance._onDeviceStateChanged.next({
          current: data.state,
          prev: deviceInstance._state
        });
      }
      deviceInstance._state = data.state;

      if (
        !this.deviceSocketSubscription ||
        this.deviceSocketSubscription.closed
      ) {
        this.deviceSocketSubscription = deviceInstance.webSocketsService.onDeviceMessage.subscribe(
          (message: IWebSocketDeviceMessage) => {
            if (message.resourceId == deviceInstance._id) {
              deviceInstance._loading = false;
              const cache = _.get(message, 'data.apiCache', false);
              if (!cache) {
                deviceInstance._online = true;
              } else if (cache &&
                (new Date().getTime() / 1000) - _.get(message, 'data.response.timestamp', 0) < 600) {
                  deviceInstance._online = true;
              } else {
                deviceInstance._online = false;
              }
              deviceInstance._onDeviceData.next(message);

              deviceInstance._onDeviceOnlineState.next({
                cached: deviceInstance.cached,
                online: deviceInstance.online
              });

              this.clearOfflineTimeout();
            }
          }
        );
      }
    }
  }

  public loadPluginsAndProcessors(deviceInstance: DeviceModel, data: any) {
    deviceInstance._processors = [];
    let plugin: SmartenitPlugin | null = null;

    // Load plugins
    if (data.hasOwnProperty('components')) {
      let pluginKey = '';
      for (let i = 0; i < data.components.length; i++) {
        const component = data.components[i];

        for (let j = 0; j < component.processors.length; j++) {
          const processor = component.processors[j];

          if (component && component.id && processor && processor.name) {
            pluginKey = DeviceModel.getPluginKey(component.id, processor.name);

            // inject energy management plugin for simple metering processors
            if (processor.name == 'SimpleMeteringServer') {
              const energyPlugin = deviceInstance.pluginFactory.createPlugin(
                'EnergyManagement',
                component.id,
                'EnergyManagement',
                deviceInstance,
                deviceInstance.devicesService
              );
              const energyPluginKey = DeviceModel.getPluginKey(
                component.id,
                'EnergyManagement'
              );

              deviceInstance._processors.push(energyPlugin);
              deviceInstance._plugins[energyPluginKey] = energyPlugin;
            }

            plugin = deviceInstance.pluginFactory.createPlugin(
              processor.name,
              component.id,
              processor.name,
              deviceInstance,
              deviceInstance.devicesService
            );

            processor.componentId = component.id;
            deviceInstance._processors.push(processor);

            deviceInstance._plugins[pluginKey] = plugin;
          }
        }
      }
    }
  }

  public getPlugin(
    componentId: string,
    processorName: string
  ): SmartenitPlugin | null {
    const pluginKey: string = DeviceModel.getPluginKey(
      componentId,
      processorName
    );

    if (this._plugins.hasOwnProperty(pluginKey)) {
      return this._plugins[pluginKey];
    }

    return null;
  }

  public getStatus(
    componentId: string,
    processorName: string,
    payload?: any,
    query?: string,
    sendDirect: boolean = false
  ): void {
    if (sendDirect) {
      query = 'cached=false';
    }

    this.executeMethod(
      componentId,
      processorName,
      'status',
      payload,
      false,
      null,
      query,
      sendDirect
    );
  }

  private innerExecuteMethod(
    componentId: string,
    processorName: string,
    method: string,
    payload?: any,
    requestStatus?: boolean,
    statusPayload?: any,
    query?: string,
    getStatusDelay: number = 0.5 * this.SECOND
  ): any {
    let path = [
      this._id,
      'comps',
      componentId,
      'procs',
      processorName,
      'methods',
      method
    ].join('/');

    if (requestStatus === undefined) {
      requestStatus = true;
    }

    if (method != 'status' && requestStatus) {
      if (method === 'UnlockDoor' || method === 'LockDoor') {
        // the doorlock takes some time to update its status after a lock/unlock call
        getStatusDelay = this.DOORLOCK_DELAY * this.SECOND;
      }
      setTimeout(() => {
        this.getStatus(
          componentId,
          processorName,
          statusPayload,
          'cached=false'
        );
      }, getStatusDelay);
    }

    if (query) {
      path += '?' + query;
    }

    return this.devicesService.post(path, payload);
  }

  private drainQueue() {
    if (!this.isReady()) {
      return;
    }

    if (this._deviceRequests) {
      const keys = Object.keys(this._deviceRequests);

      if (this.firstRequest && this._deviceRequests && keys.length > 0) {
        this.firstRequest = false;

        const firstRequest = this._deviceRequests[keys[0]];

        if (firstRequest) {
          this.drainRequest(firstRequest, keys[0]);
        }
      } else if (this.online && keys.length > 0) {
        for (let i = 0; i < keys.length; i++) {
          const request = this._deviceRequests[keys[i]];
          if (request) {
            this.drainRequest(request, keys[i]);
          }
        }
      }
    }
  }

  private drainRequest(request: Observable<any>, key: any) {
    if (!this.isReady()) {
      return;
    }

    const subscription = request.subscribe((response: any) => {
      this._onDeviceResponse.next({ response, key });
      if (subscription) {
        subscription.unsubscribe();
      }
    });

    delete this._deviceRequests[key];
  }

  private addToQueue(
    componentId: string,
    processorName: string,
    method: string,
    payload?: any,
    requestStatus?: boolean,
    payloadStatus?: any,
    query?: string,
    sendDirect: boolean = false,
    getStatusDelay?: number
  ) {
    const path = [componentId, processorName, 'methods', method].join('-');

    if (!this._deviceRequests) {
      this._deviceRequests = {};
    }

    if (this._deviceRequests[path] || sendDirect) {
      this._deviceRequests[path] = this.innerExecuteMethod(
        componentId,
        processorName,
        method,
        payload,
        requestStatus,
        payloadStatus,
        query,
        getStatusDelay
      );
      this.drainRequest(this._deviceRequests[path], path);
    } else {
      this._deviceRequests[path] = this.innerExecuteMethod(
        componentId,
        processorName,
        method,
        payload,
        requestStatus,
        payloadStatus,
        query,
        getStatusDelay
      );
    }
  }

  public executeMethod(
    componentId: string,
    processorName: string,
    method: string,
    payload?: any,
    requestStatus?: boolean,
    payloadStatus?: any,
    query?: string,
    sendDirect: boolean = false,
    getStatusDelay?: number
  ): void {
    this.startOfflineTimeout();

    const path = [
      this._id,
      'comps',
      componentId,
      'procs',
      processorName,
      'methods',
      method
    ].join('/');

    if (payload === null) {
      payload = {};
    }

    this.addToQueue(
      componentId,
      processorName,
      method,
      payload,
      requestStatus,
      payloadStatus,
      query,
      sendDirect,
      getStatusDelay
    );
    this.drainQueue();
  }

  public getAttribute(
    componentId: string,
    processorName: string,
    attribute: string,
    payload?: any,
    query?: string
  ): Observable<any> {
    if (!this.isReady()) {
      return Observable.empty();
    }

    this.startOfflineTimeout();

    let path = [
      this._id,
      'comps',
      componentId,
      'procs',
      processorName,
      'attrs',
      attribute
    ].join('/');

    if (query) {
      path += '?' + query;
    }

    return this.devicesService.get(path, payload).map((response: any) => {
      this._onDeviceResponse.next({
        key: [componentId, processorName, 'attrs', attribute].join('-'),
        response
      });
      return response;
    });
  }

  public setAttribute(
    componentId: string,
    processorName: string,
    attribute: string,
    value: any,
    readAttribute?: boolean,
    readAttributeDelay: number = 500
  ): Observable<any> {
    if (!this.isReady()) {
      return Observable.empty();
    }

    this.startOfflineTimeout();

    const path = [
      this._id,
      'comps',
      componentId,
      'procs',
      processorName,
      'attrs',
      attribute
    ].join('/');

    if (readAttribute === undefined) {
      readAttribute = true;
    }

    if (readAttribute) {
      setTimeout(() => {
        this.getAttribute(
          componentId,
          processorName,
          attribute,
          null,
          'cached=false'
        ).subscribe((response) => {
          this._onDeviceResponse.next({ response, key: path });
        });
      }, readAttributeDelay);
    }

    return this.devicesService.put(path, value).map((response) => {
      this._onDeviceResponse.next({ response, key: path });

      return response;
    });
  }

  public getProcessor(name: string): any {
    for (let i = 0; i < this._processors.length; i++) {
      const processor = this._processors[i];
      if (processor && processor.name === name) {
        return processor;
      }
    }

    return null;
  }

  public getProcessors(name: string): any[] {
    return this._processors.filter((processor: any) => {
      return processor && processor.name === name;
    });
  }

  public getComponent(id: string): any {
    for (let i = 0; i < this._data.components.length; i++) {
      const component = this._data.components[i];
      if (component && component.id === id) {
        return component;
      }
    }

    return null;
  }

  public isChildrenOf(id: string, hwId: string) {
    return (
      this._ownerId === hwId ||
      (this._parents &&
        this._parents.devices &&
        this._parents.devices.some((p: any) => p.id === id))
    );
  }

  public isReady() {
    // TODO (joha.l) The condition for the iot4r is temporal. It is necesary to set the state of this model to READY. Probably in CAPI.
    return (
      (this._state &&
        (<any> DeviceState)[this._state.toUpperCase()] === DeviceState.READY) ||
      this._model == 'iot4r'
    );
  }

  private startOfflineTimeout() {
    if (this._onlineTimeout === null) {
      this._onlineTimeout = setTimeout(() => {
        this._online = false;
        this.firstRequest = true;
        this._onDeviceOnlineState.next({ online: false });
      }, 60 * 10 * 1000);
    }
  }

  private clearOfflineTimeout() {
    if (this._onlineTimeout !== null) {
      clearTimeout(this._onlineTimeout);
      this._onlineTimeout = null;
    }
  }

  public clearResources() {
    if (this.queueInterval) {
      clearInterval(this.queueInterval);
    }

    if (this.deviceSocketSubscription) {
      this.deviceSocketSubscription.unsubscribe();
    }
  }

  public getAttributeReport(
    componentId: string,
    processorName: string,
    attribute: string,
    payload?: any,
    query?: string
  ): Observable<any> {
    if (!this.isReady()) {
      return Observable.empty();
    }

    this.startOfflineTimeout();

    let path = [
      this._id,
      'comps',
      componentId,
      'procs',
      processorName,
      'reports',
      attribute
    ].join('/');

    if (query) {
      path += '?' + query;
    } else {
      path += '?' + 'cached=false&timeout=3';
    }

    return this.devicesService.get(path, payload).map((response: any) => {
      this._onDeviceResponse.next({
        key: [componentId, processorName, 'reports', attribute].join('-'),
        response
      });
      return response;
    });
  }

  public setAttributeReport(
    componentId: string,
    processorName: string,
    attribute: string,
    value: any,
    readAttributeReport?: boolean,
    readAttributeDelay: number = 500
  ): Observable<any> {
    if (!this.isReady()) {
      return Observable.empty();
    }

    this.startOfflineTimeout();

    const path = [
      this._id,
      'comps',
      componentId,
      'procs',
      processorName,
      'reports',
      attribute
    ].join('/');

    if (readAttributeReport === undefined) {
      readAttributeReport = true;
    }

    if (readAttributeReport) {
      setTimeout(() => {
        this.getAttributeReport(
          componentId,
          processorName,
          attribute,
          null,
          'cached=false'
        ).subscribe((response) => {
          this._onDeviceResponse.next({ response, key: [componentId, processorName, 'reports', attribute].join('-') });
        });
      }, readAttributeDelay);
    }

    return this.devicesService.put(path, value).map((response) => {
      this._onDeviceResponse.next({ response, key: [componentId, processorName, 'reports', attribute].join('-') });
      return response;
    });
  }
}
